import type React from "react";
import "./CurrentTenantPaymentList.css";
import {
  EntityType,
  PaidFlowTypesEnum,
  PaymentDueAccountVM,
  PaymentMethodTypesEnum,
  PaymentService,
  TenantService,
} from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import { PubSub, TopicsEnum } from "../../utils/pubsub";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import editIcon from "../../assets/componentsIcons/dropdown/edit.svg";
import deleteIcon from "../../assets/componentsIcons/dropdown/delete.svg";
import dropdownIcon from "../../assets/componentsIcons/dropdown/dropdown.svg";
import { ConfirmationPopup } from "../confirmation-popup/confirmation-popup-demo";
import RightSideModal from "../Modal/right-side-modal";
import Card from "../card";
import TruncatedDescriptionCell from "../common/TruncatedDescriptionCell";
import { FormProvider, useForm } from "react-hook-form";
import SelectField from "../form-fields/SelectField";
import InputField from "../form-fields/InputField";
import DescriptionTextarea from "../form-fields/DescriptionTextarea";
import { getEnumOptions } from "../../utils/enumHelpers";
import ActionMenu from "../actionMenus/action-menu";
import { formatWithCommas, utcDateFormat } from "../../utils/helper";
import DateHelper from "../../utils/DateHelper";
import SimpleButton from "../buttons/Simple-button";
import Radio from "../form-fields/radio";
import UtilHelper from "../../utils/UtilHelper";
import RadioGroup from "../form-fields/RadioGroup";
import SimpleSpinner from "../../components/loader";
import EmptyState from "../emptystate";
import { GetImages } from "../../utils/GetImages";
import H2 from "../typography/H2";
import CurrencyInput from "../form-fields/CurrencyInput";
import AddPaymentModal from "./AddPaymentModal";
import CurrencyTooltip from "../../components/currencytooltip/CurrencyTooltip";
import { useCurrencySymbol } from "../../hooks/useCurrencySymbol";
import { usePermissions } from "../../hooks/usePermissions";
import {
  usePaymentSubmitGuard,
  PayPaymentDueAccountWithKey,
  NextIdempotencyKey,
} from "../../hooks/usePaymentSubmitGuard";
import { t } from "i18next";
import BluePayableIcon from "../../assets/Payment-Icon.svg";
import { useFieldChangesSnapshot } from "../../hooks/useFieldChangesSnapshot";

export interface Payment {
  id?: number;
  payerPayeeName?: string | null;
  description?: string | null;
  dueDate?: string;
  amount?: number;
  streetAddress?: string | null;
  isEntity?: boolean;
  entityId?: number;
  entityTypeId?: EntityType;
  className?: string;
  detailsActiveTab?: string;
  refreshTrigger?: number;
  onRefresh?: () => void;
  expensesDueRaw?: number | string;
  expensesDue?: number | string;
}

// Payment recording modes -- mirrors PaymentMethodTypesEnum on the backend.
// This drawer always posts a tenant's own rent receivable, so an Organization
// can never Plaid-pay it -- only the manual recording modes apply.
const PAYMENT_MODE = PaymentMethodTypesEnum;
const PAYMENT_MODE_OPTIONS = getEnumOptions(PAYMENT_MODE, [
  PAYMENT_MODE.Cash,
  PAYMENT_MODE.Check,
  PAYMENT_MODE.Other,
  PAYMENT_MODE.Assisted,
]);

const CurrentTenantPaymentList: React.FC<Payment> = ({
  refreshTrigger,
  onRefresh,
  expensesDue,
  expensesDueRaw,
}) => {
  const { id } = useParams(); // Gets the Tenant ID from URL
  const currencySymbol = useCurrencySymbol();
  const [payments, setPayments] = useState<PaymentDueAccountVM[]>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const { isSubmitting: isPaySubmitting, runGuarded: runPayGuarded } =
    usePaymentSubmitGuard();
  const [showConfirmation, setShowConfirmation] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [isWaiveModalOpen, setWaiveModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [tenantData, setTenantData] = useState<any>(null);
  const [selectedPayment, setSelectedPayment] =
    useState<PaymentDueAccountVM | null>(null);
  const [paymentTypes, setPaymentTypes] = useState<
    { Id: number; Name: string }[]
  >([]);

  const [paymentModalConfig, setPaymentModalConfig] = useState({
    isOpen: false,
    entityId: null as number | null,
    entityType: EntityType.Tenant,
  });

  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      paymentType: "full",
      partialAmount: "",
      paymentMethodTypeId: "",
      checkNum: "",
      transactionId: "",
      assistanceProvider: "",
      date: "",
    },
  });

  const waiveMethods = useForm({
    mode: "onChange",
    defaultValues: {
      waiveType: "full",
      partialAmount: "",
    },
  });

  const editMethods = useForm({ mode: "onChange" });

  // Save must re-disable once an edited field is typed back to its loaded value --
  // editMethods.formState.isDirty alone stays true once any field has ever been touched
  // (see ui-consistency.md SS10 gotcha 9). ANDed with isDirty below, not a replacement.
  const { hasFieldChanges: editHasFieldChanges, setSnapshot: setEditSnapshot } =
    useFieldChangesSnapshot<{
      amount: number | string;
      dueDate: string;
      paymentTypeId: number | string;
      description: string;
    }>(editMethods.control, ["amount", "dueDate", "paymentTypeId", "description"]);

  const navigate = useNavigate();
  const { canWrite } = usePermissions();
  const hasPaymentsWriteAccess = canWrite("Payments");

  const formId = "post-payment-form";
  const waiveFormId = "waive-payment-form";
  const editFormId = "edit-payment-form";

  const {
    formState: { isDirty, isSubmitting, isValid },
    handleSubmit,
    watch,
    reset,
  } = methods;

  const paymentType = watch("paymentType");
  const partialAmountInput = watch("partialAmount");
  const selectedMode = Number(watch("paymentMethodTypeId"));

  const waiveType = waiveMethods.watch("waiveType");
  const partialAmount = waiveMethods.watch("partialAmount");

  const getMenuItems = (payment: PaymentDueAccountVM) => [
    {
      icon: editIcon,
      label: "Edit",
      action: () => {
        setSelectedPayment(payment);
        const editResetValues = {
          amount: payment.amount,
          dueDate: payment.dueDate
            ? DateHelper.formatDate(payment.dueDate)
            : "",
          paymentTypeId: payment.paymentTypeId,
          description: payment.description || "",
        };
        editMethods.reset(editResetValues);
        setEditSnapshot(editResetValues);
        setEditModalOpen(true);
      },
      isDivider: false,
      isDestructive: false,
    },
    { isDivider: true },
    {
      icon: deleteIcon,
      label: "Delete",
      action: () => {
        setSelectedPayment(payment);
        setShowConfirmation("delete");
      },
      isDivider: false,
      isDestructive: false,
    },
    { isDivider: true },
    {
      icon: BluePayableIcon,
      label: "Post Payment",
      action: () => {
        setSelectedPayment(payment);
        reset({
          paymentType: "full",
          partialAmount: "",
          paymentMethodTypeId: "",
          checkNum: "",
          transactionId: "",
          assistanceProvider: "",
          date: new Date().toISOString().split("T")[0],
        });
        setModalOpen(true);
      },
      isDivider: false,
      isDestructive: false,
    },
    { isDivider: true },
    {
      icon: BluePayableIcon,
      label: "Waive Payment",
      action: () => {
        setSelectedPayment(payment);
        waiveMethods.reset({
          waiveType: "full",
          partialAmount: "",
        });
        setWaiveModalOpen(true);
      },
      isDivider: false,
      isDestructive: false,
    },
  ];

  const permittedMenuItems = (payment: PaymentDueAccountVM) => {
    return hasPaymentsWriteAccess ? getMenuItems(payment) : [];
  };

  const deletePayment = async () => {
    if (!selectedPayment?.id || isDeleting) return;

    setIsDeleting(true);
    try {
      await apiHelper(
        PaymentService.deleteApiPaymentDueAccount,
        {
          successIcon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />,
          showNotification: true,
          successMessage: "Payment Deleted Successfully",
        },
        selectedPayment.id
      );
      getPayments();
      setShowConfirmation(null);
      setSelectedPayment(null);
      if (onRefresh) onRefresh();
      PubSub.publish(TopicsEnum.RefreshLeaseData.toString(), null);
    } catch (err) {
      console.error("Delete Payment Error:", err);
      setShowConfirmation(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchCurrentTenantDetails = () => {
    if (id) {
      apiHelper(TenantService.getApiTenant1, { showNotification: false }, id)
        .then((res) => {
          if (res?.data?.success) {
            setTenantData(res.data.success);
            setShowConfirmation(null);
          }
        })
        .catch((err) => {
          console.error("Error fetching tenants:", err);
          setShowConfirmation(null);
        });
    }
  };

  const getPaymentTypes = async () => {
    try {
      const res = await apiHelper(
        PaymentService.getApiPayeeAccountPaymentTypes,
        { showNotification: false },
        PaidFlowTypesEnum.Receiveable
      );
      const rawItems = res?.data?.success || [];
      if (rawItems.length) {
        setPaymentTypes(
          rawItems.map((item: any) => ({
            Id: item.id,
            Name: item.name,
          }))
        );
      } else {
        setPaymentTypes([]);
      }
    } catch (err) {
      console.error("Payment Types API Error:", err);
    }
  };

  const getPayments = async () => {
    setLoading(true);
    try {
      const res = await apiHelper(
        PaymentService.getApiPaymentDueAccountTenantlist,
        { showNotification: false },
        id
      );
      const rawItems = res?.data?.success || [];
      setPayments(rawItems.length ? rawItems : []);
    } catch (err) {
      console.error("Payments API Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const postPaymentWithKey = async (
    formData: any,
    nextKey: NextIdempotencyKey,
  ) => {
    if (!selectedPayment) return;

    const finalAmount =
      formData.paymentType === "partial"
        ? Number(formData.partialAmount)
        : Number(selectedPayment.amount);

    if (finalAmount <= 0) return;

    setLoading(true);
    try {
      const paymentMethodTypeId = Number(formData.paymentMethodTypeId);

      const payload: PayPaymentDueAccountWithKey = {
        payDueAccountId: Number(selectedPayment.id),
        amount: finalAmount,
        paymentMethodTypeId,
        paymentDate: utcDateFormat(formData.date),
        idempotencyKey: nextKey(),
      };

      if (paymentMethodTypeId === PAYMENT_MODE.Check) {
        payload.checkNumber = formData.checkNum;
      } else if (paymentMethodTypeId === PAYMENT_MODE.Other) {
        payload.transactionId = formData.transactionId;
      } else if (paymentMethodTypeId === PAYMENT_MODE.Assisted) {
        payload.assistanceProvider = formData.assistanceProvider;
      }

      await apiHelper(
        PaymentService.putApiPaymentDueAccountPay,
        {
          successIcon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />,
          showNotification: true,
          successMessage: "Payment Posted Successfully",
        },
        payload
      );
      getPayments();
      if (onRefresh) onRefresh();
      PubSub.publish(TopicsEnum.RefreshLeaseData.toString(), null);
      setModalOpen(false);
    } catch (err) {
      console.error("Post Payment Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const postPayments = (formData: any) =>
    runPayGuarded((nextKey) => postPaymentWithKey(formData, nextKey));

  const waivePayment = async (formData: any) => {
    if (!selectedPayment?.id) return;
    setLoading(true);
    try {
      const waiveAmount =
        formData.waiveType === "full"
          ? selectedPayment.amount
          : Number(formData.partialAmount);

      const payload = {
        payDueAccountId: selectedPayment.id,
        amount: waiveAmount,
      };

      await apiHelper(
        PaymentService.putApiPaymentDueAccountWaive,
        {
          successIcon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />,
          showNotification: true,
          successMessage: "Payment Waived Successfully",
        },
        payload
      );

      getPayments();
      if (onRefresh) onRefresh();
      PubSub.publish(TopicsEnum.RefreshLeaseData.toString(), null);
      setWaiveModalOpen(false);
    } catch (err) {
      console.error("Waive Payment Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const editPayment = async (formData: any) => {
    if (!selectedPayment?.id) return;
    setLoading(true);
    try {
      // PaymentDueAccountStatusId is server-owned (PaymentDueAccountVM.Validate rejects any
      // nonzero value from the client) and recomputed by PaymentDueAccountService.UpdateAsync
      // from the paid total -- omit it from the spread so Save doesn't send a value back.
      const { paymentDueAccountStatusId, ...restOfSelectedPayment } = selectedPayment;
      const payload: PaymentDueAccountVM = {
        ...restOfSelectedPayment,
        amount: Number(formData.amount),
        dueDate: utcDateFormat(formData.dueDate),
        paymentTypeId: Number(formData.paymentTypeId),
        description: formData.description || "",
      };

      await apiHelper(
        PaymentService.putApiPaymentDueAccount,
        {
          title: "Payment Updated",
          successMessage: "Payment updated successfully",
          failureMessage: "Unable to update payment",
          showNotification: true,
          successIcon: (
            <img
              src={GetImages.toastPaymentAccountIcon}
              alt="success"
              width="20"
              height="20"
            />
          ),
        },
        payload
      );

      getPayments();
      if (onRefresh) onRefresh();
      PubSub.publish(TopicsEnum.RefreshLeaseData.toString(), null);
      setEditModalOpen(false);
    } catch (err) {
      console.error("Edit Payment Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getPayments();
      getPaymentTypes();
      fetchCurrentTenantDetails();
    }
  }, [id]);

  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      getPayments();
    }
  }, [refreshTrigger]);

  const displayPayAmount =
    paymentType === "partial"
      ? Number(partialAmountInput) || 0
      : selectedPayment?.amount || 0;

  const outstandingAmount = selectedPayment
    ? (selectedPayment.amount || 0) - displayPayAmount
    : 0;

 if (loading) {
    return (
     <SimpleSpinner myClassName={"Icon50"} isFullPage={true} />
    );
  }

  return (
    <>
    
        <div className="payments-content">
          <div className="top-listing-header-top tenant-additional-top-listing-header-top">
            <H2 text="Payments" />
            {hasPaymentsWriteAccess && (
              <SimpleButton
                label="Add Payment"
                onClick={() => {
                  setPaymentModalConfig({
                    isOpen: true,
                    entityId: Number(id),
                    entityType: EntityType.Tenant,
                  });
                }}
              />
            )}
          </div>

          <div className="table-container current-tenant-table">
            { payments.length > 0 ? (
              <table className="payments-table">
                <thead>
                  <tr>
                    <th>Address</th>
                    <th>Description</th>
                    <th>Due Date</th>
                    <th>Amount</th>
                    {hasPaymentsWriteAccess && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id}>
                      <td>{payment.streetAddress || "-"}</td>
                      <td><TruncatedDescriptionCell description={payment.description} /></td>
                      <td>{payment.formatted?.DueDateDateOnly || "-"}</td>
                      <td>
                        <CurrencyTooltip
                          content={payment.formatted?.AmountRaw || "-"}
                          formatted={payment.formatted?.Amount || "-"}
                        >
                          <span>{payment.formatted?.Amount || "-"}</span>
                        </CurrencyTooltip>
                      </td>
                      {hasPaymentsWriteAccess && (
                        <td>
                          {permittedMenuItems(payment).length > 0 && (
                            <ActionMenu
                              menuItems={permittedMenuItems(payment)}
                              trigger={
                                <button
                                  className="action-button"
                                  aria-label="More options"
                                >
                                  <img
                                    src={dropdownIcon}
                                    alt="More options"
                                    width={20}
                                    height={20}
                                    className="action-icon"
                                  />
                                </button>
                              }
                            />
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state-wrapper w-100 d-flex justify-content-center py-5">
                <EmptyState
                  icon={GetImages.emptyStatePayment}
                  heading="No Payments Found"
                  isButton={false}
                  isBorder={false}
                />
              </div>
            )}
          </div>

          {payments.length > 0 && (
            <div className="total-amount-card">
              <div className="total-amount-content">
                <span className="total-label">Total Amount Due</span>
                <div className="total-actions">
                  <CurrencyTooltip
                    content={expensesDueRaw}
                    formatted={expensesDue}
                  >
                    <span className="total-value">{expensesDue}</span>
                  </CurrencyTooltip>
                </div>
              </div>
            </div>
          )}
        </div>
      

      {/* Delete Confirmation Popup */}
      {showConfirmation === "delete" && selectedPayment && (
        <ConfirmationPopup
          isOpen={true}
          title="Delete Payment"
          message={`Do you want to delete this payment of ${currencySymbol}${selectedPayment.amount?.toFixed(
            2
          )}?`}
          primaryButtonLabel="Delete"
          secondaryButtonLabel="Cancel"
          onPrimaryAction={deletePayment}
          onSecondaryAction={() => setShowConfirmation(null)}
          onClose={() => setShowConfirmation(null)}
          isPrimaryDestructive={true}
          isLoading={isDeleting}
          loadingLabel="Deleting..."
        />
      )}

      {/* Add Payment Modal Component */}
      {paymentModalConfig.isOpen && (
        <AddPaymentModal
          isOpen={paymentModalConfig.isOpen}
          onClose={() => {
            setPaymentModalConfig((prev) => ({ ...prev, isOpen: false }));
            getPayments();
            if (onRefresh) onRefresh();
            PubSub.publish(TopicsEnum.RefreshLeaseData.toString(), null);
          }}
          entityIdProp={paymentModalConfig.entityId || undefined}
          entityTypeIdProp={paymentModalConfig.entityType}
          defaultPaymentMode="2"
        />
      )}

      {/* Post Payment Modal */}
      <RightSideModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title="Post Payment"
        buttonlabel="Save"
        formId={formId}
        maxWidth={550}
        isDirty={isDirty}
        isSubmitDisabled={isSubmitting || isPaySubmitting || !isDirty || !isValid}
      >
        <Card className="no-bg">
          <div className="tenant-info">
            {tenantData && selectedPayment && (
              <>
                <div className="tenant-left">
                  <div className="tenant-avatar">
                    <div className="tenant-initials">
                      {UtilHelper.getInitials(
                        tenantData?.firstName + " " + tenantData?.lastName
                      )}
                    </div>
                  </div>
                  <div className="tenant-name">
                    {tenantData?.firstName + " " + tenantData?.lastName}
                    <p className="tenant-payment-type">Rent</p>
                  </div>
                </div>
                <div className="tenant-amount">
                  <CurrencyTooltip
                    content={selectedPayment?.formatted?.AmountRaw}
                    formatted={selectedPayment?.formatted?.Amount}
                  >
                    <span>{selectedPayment?.formatted?.Amount}</span>
                  </CurrencyTooltip>
                </div>
              </>
            )}
          </div>
          <FormProvider {...methods}>
            <form id={formId} onSubmit={handleSubmit(postPayments)}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <RadioGroup>
                  <Radio name="paymentType" value="full" label="Full" />
                  <Radio name="paymentType" value="partial" label="Partial" />
                </RadioGroup>

                {paymentType === "partial" && (
                  <div style={{ width: "120px" }}>
                    <CurrencyInput
                      name="partialAmount"
                      required={paymentType === "partial"}
                    />
                  </div>
                )}
              </div>

              {paymentType === 'partial' && (
                <div style={{
                  backgroundColor: '#F8F9FB',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  border: '1px solid #E2E8F0'
                }}>
                  <span style={{ fontSize: '14px', fontWeight: 500, color: '#1E293B' }}>Outstanding Amount</span>
                  <CurrencyTooltip
                    content={formatWithCommas(outstandingAmount.toFixed(2))}
                    formatted={UtilHelper.formatCurrency(outstandingAmount, false, currencySymbol)}
                  >
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#1E293B' }}>
                      {UtilHelper.formatCurrency(outstandingAmount, false, currencySymbol)}
                    </span>
                  </CurrencyTooltip>
                </div>
              )}

              <div className="row">
                <div className="col-md-6">
                  <SelectField
                    name="paymentMethodTypeId"
                    label="Select Payment Mode"
                    required
                    requiredMessage="Please select a payment mode"
                    options={PAYMENT_MODE_OPTIONS}
                    placeholderText="Select Payment Mode"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="col-md-6">
                  <InputField
                    name="date"
                    label="Date of Payment"
                    type="date"
                    required
                    requiredMessage="Payment Date is required"
                  />
                </div>
              </div>

              {selectedMode === PAYMENT_MODE.Check && (
                <div className="row mt-3">
                  <div className="col-md-6">
                    <InputField
                      name="checkNum"
                      label="Check No"
                      type="text"
                      required
                      requiredMessage="Check No is required"
                    />
                  </div>
                </div>
              )}
              {selectedMode === PAYMENT_MODE.Other && (
                <div className="row mt-3">
                  <div className="col-md-6">
                    <InputField
                      name="transactionId"
                      label="Transaction ID"
                      type="text"
                      required
                      requiredMessage="Transaction ID is required"
                    />
                  </div>
                </div>
              )}
              {selectedMode === PAYMENT_MODE.Assisted && (
                <div className="row mt-3">
                  <div className="col-md-6">
                    <InputField
                      name="assistanceProvider"
                      label="Assistance Provider"
                      type="text"
                      required
                      requiredMessage="Assistance Provider is required"
                      allowSpecialChars={true}
                    />
                  </div>
                </div>
              )}
            </form>
            <div className="payment-alert">
              <p>You've Received</p>
              <p className="payment-divider">------------------------</p>
              <CurrencyTooltip
                content={UtilHelper.CurrencyFormat(displayPayAmount, false)}
                formatted={UtilHelper.formatCurrency(displayPayAmount, false, currencySymbol)}
              >
                <span>{UtilHelper.formatCurrency(displayPayAmount, false, currencySymbol)}</span>
              </CurrencyTooltip>
            </div>
          </FormProvider>
        </Card>
      </RightSideModal>

      {/* Waive Payment Modal */}
      <RightSideModal
        isOpen={isWaiveModalOpen}
        onClose={() => setWaiveModalOpen(false)}
        title="Waive Payment"
        buttonlabel="Waive"
        formId={waiveFormId}
        maxWidth={550}
        isDirty={waiveMethods.formState.isDirty}
        isSubmitDisabled={waiveMethods.formState.isSubmitting || !waiveMethods.formState.isDirty || !waiveMethods.formState.isValid}
      >
        <Card className="no-bg">
          <div className="tenant-info">
            {tenantData && selectedPayment && (
              <>
                <div className="tenant-left">
                  <div className="tenant-avatar">
                    <div className="tenant-initials">
                      {UtilHelper.getInitials(
                        tenantData?.firstName + " " + tenantData?.lastName
                      )}
                    </div>
                  </div>
                  <div className="tenant-name">
                    {tenantData?.firstName + " " + tenantData?.lastName}
                    <p className="tenant-payment-type">Rent</p>
                  </div>
                </div>
                <div className="tenant-amount">
                  <CurrencyTooltip
                    content={selectedPayment?.formatted?.AmountRaw}
                    formatted={selectedPayment?.formatted?.Amount}
                  >
                    <span>{selectedPayment?.formatted?.Amount}</span>
                  </CurrencyTooltip>
                </div>
              </>
            )}
          </div>

          <FormProvider {...waiveMethods}>
            <form
              id={waiveFormId}
              onSubmit={waiveMethods.handleSubmit(waivePayment)}
            >
              <div className="row">
                <div className="col-md-12">
                  <RadioGroup label="Waive Type" required>
                    <Radio name="waiveType" value="full" label="Full" />
                    <Radio name="waiveType" value="partial" label="Partial" />
                  </RadioGroup>
                </div>
              </div>

              {waiveType === "partial" && (
                <div className="row mt-3">
                  <div className="col-md-12">
                    <CurrencyInput
                      name="partialAmount"
                      label="Partial Amount"
                      placeholder="Enter amount to waive"
                      required
                      requiredMessage="Please enter partial amount"
                      disabled={waiveMethods.formState.isSubmitting}
                    />
                  </div>
                </div>
              )}
            </form>

            <div className="payment-alert">
              <p>Waive Amount:</p>
              <p className="payment-divider">------------------------</p>
              <CurrencyTooltip
                content={waiveType === "full"
                  ? selectedPayment?.formatted?.AmountRaw
                  : partialAmount
                    ? formatWithCommas(partialAmount)
                    : "0.00"}
                formatted={waiveType === "full"
                  ? selectedPayment?.formatted?.Amount
                  : partialAmount
                    ? UtilHelper.formatCurrency(partialAmount, false, currencySymbol)
                    : "0.00"}
              >
                <p>
                {waiveType === "full"
                  ? selectedPayment?.formatted?.Amount
                  : partialAmount
                    ? UtilHelper.formatCurrency(partialAmount, false, currencySymbol)
                    : "0.00"}
              </p>
              </CurrencyTooltip>
            </div>
          </FormProvider>
        </Card>
      </RightSideModal>

      {/* Edit Payment Modal */}
      <RightSideModal
        isOpen={isEditModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Edit Payment"
        buttonlabel="Save"
        formId={editFormId}
        maxWidth={550}
        isDirty={editMethods.formState.isDirty && editHasFieldChanges}
        isSubmitDisabled={
          editMethods.formState.isSubmitting ||
          !(editMethods.formState.isDirty && editHasFieldChanges) ||
          !editMethods.formState.isValid
        }
      >
        <Card className="no-bg">
          <FormProvider {...editMethods}>
            <form
              id={editFormId}
              onSubmit={editMethods.handleSubmit(editPayment)}
            >
              <div className="row">
                <div className="col-md-6">
                  <CurrencyInput
                    name="amount"
                    label="Amount"
                    placeholder="Enter amount"
                    required
                    requiredMessage="Amount is required"
                    disabled={editMethods.formState.isSubmitting}
                  />
                </div>

                <div className="col-md-6">
                  <InputField
                    name="dueDate"
                    label="Due Date"
                    type="date"
                    required
                    requiredMessage="Due Date is required"
                    disabled={editMethods.formState.isSubmitting}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-12">
                  <SelectField
                    name="paymentTypeId"
                    label="Payment Type"
                    required
                    requiredMessage="Payment Type is required"
                    options={paymentTypes}
                    placeholderText="Select Payment Type"
                    disabled={editMethods.formState.isSubmitting}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-12">
                  <DescriptionTextarea
                    name="description"
                    label="Description"
                    placeholder="Description"
                    required
                    requiredMessage="Description is required"
                    disabled={editMethods.formState.isSubmitting}
                    data-testid="field-description"
                    alignCounterWithError
                    validation={{
                      maxLength: {
                        value: 250,
                        message: "Description cannot exceed 250 characters",
                      },
                    }}
                  />
                </div>
              </div>
            </form>
          </FormProvider>
        </Card>
      </RightSideModal>
    </>
  );
};

export default CurrentTenantPaymentList;